import { useEffect, useRef } from 'react';

import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { IcoCheck } from '@myrealtrip/web-icons';
import { Text, UnstyledButton } from '@myrealtrip/web-ui';

import Show from '~components/Show';
import { AGE_LIST } from '~constants/pointToPointTicket';

function getAgeText(age: number, index: number) {
  const isLast = AGE_LIST.length - 1 === index;

  return isLast ? `만 ${age}세 이상` : `만 ${age}세`;
}

interface Props {
  travelerIndex: number;
  selectedAge?: number;
  onClickOption: (travelerIndex: number, age: number) => () => void;
}

const GUIDE_ITEM_GAP = 8;

const TravelerAgeSelectorOptionList = ({ travelerIndex, selectedAge, onClickOption }: Props) => {
  const { colors } = useTheme();
  const optionListRef = useRef<HTMLDivElement>(null);
  const guideItemHeightRef = useRef<number>(0);

  useEffect(() => {
    if (optionListRef && optionListRef.current) {
      const selectedElement = optionListRef.current.querySelector(`[data-selected="true"]`);

      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'start' });
        optionListRef.current.scrollTop -= guideItemHeightRef.current + GUIDE_ITEM_GAP;
      }
    }
  }, []);

  return (
    <OptionListContainer ref={optionListRef}>
      <GuideItem ref={(node) => (guideItemHeightRef.current = node?.offsetHeight || 0)}>
        <GuideText as="p" typography="paragraphNormal14">
          나이는 탑승일 기준으로 선택해주세요.
        </GuideText>
      </GuideItem>
      {AGE_LIST.map((age, index) => (
        <OptionItem
          key={age}
          type="button"
          data-selected={selectedAge === age}
          isSelected={selectedAge === age}
          onClick={onClickOption(travelerIndex, age)}
        >
          <AgeText typography={selectedAge === age ? 'paragraphBold14' : 'paragraphNormal14'}>
            {getAgeText(age, index)}
          </AgeText>
          <Show when={selectedAge === age}>
            <IcoCheck size={20} color={colors.blue.s500} />
          </Show>
        </OptionItem>
      ))}
    </OptionListContainer>
  );
};

export default TravelerAgeSelectorOptionList;

const OptionListContainer = styled.div`
  position: relative;
  width: 231px;
  height: 222px;
  overflow-y: auto;

  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const GuideItem = styled.div`
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray.s80};
  padding: 9px 12px;
  margin-bottom: ${GUIDE_ITEM_GAP}px;
`;

const GuideText = styled(Text)`
  color: ${({ theme }) => theme.colors.gray.s600};
`;

const OptionItem = styled(UnstyledButton)<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 9px 12px;

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.colors.blue.s50};
      border-radius: 8px;
    `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray.s50};
    border-radius: 8px;
  }
`;

const AgeText = styled(Text)`
  color: ${({ theme }) => theme.colors.gray.s1000};
`;
